'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import {
  LuMapPin,
  LuLandmark,
  LuHouse,
  LuMonitor,
  LuFlame,
  LuPackage,
  LuBike,
  LuShield,
  LuPrinter,
  LuLaptop,
  LuGraduationCap,
  LuWifi,
  LuRuler,
  LuCompass,
  LuTarget,
  LuKey,
  LuBuilding,
  LuClipboardList,
  LuDatabase,
  LuGlobe,
  LuRadar,
  LuArrowRight,
  LuChevronRight,
} from 'react-icons/lu';

// Define the service category interface
interface ServiceCategory {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
  gradientFrom: string;
  gradientTo: string;
  services: Service[];
}

// Define the service interface
interface Service {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  features: string[];
  image: string;
  overlayColor: string;
  url: string;
}

const ServicesPage = () => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
    setActiveCategory('land-survey'); // Set default active category
  }, []);

  // Define service categories
  const serviceCategories: ServiceCategory[] = [
    {
      id: 'land-survey',
      title: 'Land Survey',
      description: 'Professional land surveying and boundary marking services',
      icon: LuMapPin,
      color: 'text-blue-400',
      gradientFrom: 'from-blue-500',
      gradientTo: 'to-blue-600',
      services: [
        {
          id: 'land-subdivision',
          title: 'Land Subdivision',
          description:
            'Professional land subdivision services with complete documentation',
          icon: LuMapPin,
          features: [
            'Area calculation',
            'Legal documentation',
            'Digital mapping',
            'Boundary marking',
          ],
          image: '/images/pages/survey-land-subdivision.jpg',
          overlayColor: 'from-orange-600/80',
          url: '/public/services/land-survey',
        },
        {
          id: 'transfer-services',
          title: 'Transfer Services',
          description: 'Seamless land transfer services with legal compliance',
          icon: LuRuler,
          features: [
            'Title verification',
            'Transfer documents',
            'Legal compliance',
            'Registry filing',
          ],
          image: '/images/pages/survey-transfer-services.jpg',
          overlayColor: 'from-amber-600/80',
          url: '/public/services/land-survey',
        },
        {
          id: 'beacon-installation',
          title: 'Beacon Installation',
          description:
            'Precise beacon installation for clear property demarcation',
          icon: LuCompass,
          features: [
            'GPS coordination',
            'Physical beacons',
            'Boundary mapping',
            'Installation report',
          ],
          image: '/images/pages/survey-beacon-installation.jpg',
          overlayColor: 'from-yellow-600/80',
          url: '/public/services/land-survey',
        },
        {
          id: 'area-confirmation',
          title: 'Area Confirmation',
          description: 'Accurate area measurement and verification services',
          icon: LuTarget,
          features: [
            'Area survey',
            'Measurement report',
            'Digital records',
            'Verification certificate',
          ],
          image: '/images/pages/survey-area-confirmation.jpg',
          overlayColor: 'from-red-600/80',
          url: '/public/services/land-survey',
        },
      ],
    },
    {
      id: 'gis-mapping',
      title: 'GIS & Mapping',
      description:
        'Geographic Information Systems and digital mapping solutions',
      icon: LuLandmark,
      color: 'text-emerald-400',
      gradientFrom: 'from-emerald-500',
      gradientTo: 'to-emerald-600',
      services: [
        {
          id: 'gis-data-collection',
          title: 'GIS Data Collection',
          description: 'Professional GIS data collection and analysis services',
          icon: LuMapPin,
          features: [
            'Field data collection',
            'Data validation',
            'Spatial analysis',
            'Data processing',
          ],
          image: '/images/pages/gis-gis-data-collection.jpg',
          overlayColor: 'from-emerald-600/80',
          url: '/public/services/gis-mapping',
        },
        {
          id: 'spatial-analysis',
          title: 'Spatial Analysis',
          description: 'Advanced spatial analysis and mapping solutions',
          icon: LuDatabase,
          features: [
            'Pattern analysis',
            'Spatial modeling',
            'Hot spot analysis',
            'Network analysis',
          ],
          image: '/images/pages/gis-spatial-analysis.jpg',
          overlayColor: 'from-blue-600/80',
          url: '/public/services/gis-mapping',
        },
        {
          id: 'remote-sensing',
          title: 'Remote Sensing',
          description: 'Remote sensing and satellite imagery interpretation',
          icon: LuGlobe,
          features: [
            'Satellite imagery',
            'Image classification',
            'Change detection',
            'Terrain analysis',
          ],
          image: '/images/pages/gis-remote-sensing.jpg',
          overlayColor: 'from-purple-600/80',
          url: '/public/services/gis-mapping',
        },
        {
          id: 'digital-mapping',
          title: 'Digital Mapping',
          description: 'Digital mapping and cartography services',
          icon: LuRadar,
          features: [
            'Thematic mapping',
            'Web mapping',
            'Custom cartography',
            'Interactive maps',
          ],
          image: '/images/pages/gis-digital-mapping.jpg',
          overlayColor: 'from-cyan-600/80',
          url: '/public/services/gis-mapping',
        },
      ],
    },
    {
      id: 'property-management',
      title: 'Property Management',
      description: 'Complete property management and maintenance services',
      icon: LuHouse,
      color: 'text-orange-400',
      gradientFrom: 'from-orange-500',
      gradientTo: 'to-orange-600',
      services: [
        {
          id: 'property-maintenance',
          title: 'Property Maintenance',
          description:
            'Comprehensive property maintenance and management services',
          icon: LuHouse,
          features: [
            'Regular inspections',
            'Preventive maintenance',
            'Emergency repairs',
            'Quality assurance',
          ],
          image: '/images/pages/property-maintenance.jpg',
          overlayColor: 'from-purple-600/80',
          url: '/public/services/property-management',
        },
        {
          id: 'tenant-management',
          title: 'Tenant Management',
          description: 'Complete tenant management and relationship services',
          icon: LuKey,
          features: [
            'Tenant screening',
            'Lease management',
            'Rent collection',
            'Tenant relations',
          ],
          image: '/images/pages/property-tenant-management.jpg',
          overlayColor: 'from-violet-600/80',
          url: '/public/services/property-management',
        },
        {
          id: 'villa-management',
          title: 'Villa Management',
          description: 'Specialized management services for luxury properties',
          icon: LuBuilding,
          features: [
            'Security management',
            'Staff supervision',
            'Amenity maintenance',
            'Guest services',
          ],
          image: '/images/pages/property-villa-management.jpg',
          overlayColor: 'from-fuchsia-600/80',
          url: '/public/services/property-management',
        },
        {
          id: 'home-services',
          title: 'Home Services',
          description: 'Comprehensive home services and delivery solutions',
          icon: LuClipboardList,
          features: [
            'Scheduled deliveries',
            'Moving assistance',
            'Setup services',
            'Quality control',
          ],
          image: '/images/pages/property-home-movers.jpg',
          overlayColor: 'from-indigo-600/80',
          url: '/public/services/property-management',
        },
      ],
    },
    {
      id: 'cyber-services',
      title: 'Cyber Services',
      description: 'IT solutions, web development, and cybersecurity services',
      icon: LuMonitor,
      color: 'text-purple-400',
      gradientFrom: 'from-purple-500',
      gradientTo: 'to-purple-600',
      services: [
        {
          id: 'printing-stationery',
          title: 'Printing & Stationery',
          description: 'High-quality printing services and office supplies',
          icon: LuPrinter,
          features: [
            'Document printing',
            'Photo printing',
            'Binding services',
            'Office supplies',
          ],
          image: '/images/pages/cyber-printing.jpg',
          overlayColor: 'from-indigo-600/80',
          url: '/public/services/cyber-services',
        },
        {
          id: 'computer-services',
          title: 'Computer Services',
          description: 'Professional computer maintenance and repair solutions',
          icon: LuLaptop,
          features: [
            'Hardware repairs',
            'Software installation',
            'Virus removal',
            'Data recovery',
          ],
          image: '/images/pages/cyber-computer-services.jpg',
          overlayColor: 'from-blue-600/80',
          url: '/public/services/cyber-services',
        },
        {
          id: 'computer-training',
          title: 'Computer Training',
          description: 'Comprehensive computer skills training programs',
          icon: LuGraduationCap,
          features: [
            'Basic computer skills',
            'Office applications',
            'Programming basics',
            'Web development',
          ],
          image: '/images/pages/cyber-computer-training.jpg',
          overlayColor: 'from-cyan-600/80',
          url: '/public/services/cyber-services',
        },
        {
          id: 'internet-services',
          title: 'Internet Services',
          description: 'High-speed internet and online solutions',
          icon: LuWifi,
          features: [
            'Web browsing',
            'Email services',
            'Online research',
            'File downloads',
          ],
          image: '/images/pages/cyber-internet-services.jpg',
          overlayColor: 'from-sky-600/80',
          url: '/public/services/cyber-services',
        },
      ],
    },
    {
      id: 'qwetu-gas-supply',
      title: 'Qwetu Gas Supplier',
      description: 'Cylinders, refills, and free home delivery',
      icon: LuFlame,
      color: 'text-orange-400',
      gradientFrom: 'from-orange-500',
      gradientTo: 'to-amber-600',
      services: [
        {
          id: 'cylinder-refill',
          title: 'Cylinder Refill',
          description:
            'Same-day refills for K-Gas, Sea Gas, Pro Gas, Afrigas and more',
          icon: LuFlame,
          features: [
            '6 kg and 13 kg cylinders',
            'All major brands',
            'Quality-checked seals',
            'Receipt with every refill',
          ],
          image: '/images/pages/qwetu-gas/products-collage.jpg',
          overlayColor: 'from-orange-600/80',
          url: '/public/services/qwetu-gas-supply',
        },
        {
          id: 'new-cylinder-kits',
          title: 'New Cylinder Kits',
          description: 'Brand-new cylinders bundled with hose, regulator, and clips',
          icon: LuPackage,
          features: [
            'Full starter kit',
            'Genuine accessories',
            'Pressure-tested',
            'Warranty-backed brands',
          ],
          image: '/images/pages/qwetu-gas/storefront.jpg',
          overlayColor: 'from-blue-600/80',
          url: '/public/services/qwetu-gas-supply',
        },
        {
          id: 'stoves-accessories',
          title: 'Stoves & Accessories',
          description:
            'Burners, two-burner stoves, hose kits, LED bulbs and cables',
          icon: LuShield,
          features: [
            'Burner heads',
            'Two-burner cookers',
            'Regulators and hoses',
            'Small electronics',
          ],
          image: '/images/pages/qwetu-gas/shopfront-wide.jpg',
          overlayColor: 'from-emerald-600/80',
          url: '/public/services/qwetu-gas-supply',
        },
        {
          id: 'home-delivery',
          title: 'Free Home Delivery',
          description: 'We bring the cylinder to your door at no extra charge',
          icon: LuBike,
          features: [
            'Same-day delivery',
            'No delivery fee',
            'Cash or M-Pesa on arrival',
            'Friendly riders',
          ],
          image: '/images/pages/qwetu-gas/delivery-rider.jpg',
          overlayColor: 'from-amber-600/80',
          url: '/public/services/qwetu-gas-supply',
        },
      ],
    },
  ];

  // Get active category data
  const activeCategoryData = serviceCategories.find(
    (category) => category.id === activeCategory
  );

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <div className='min-h-screen bg-gray-900'>
      {/* Hero Section */}
      <div className='relative h-[40vh] sm:h-[50vh] overflow-hidden'>
        <div className='absolute inset-0'>
          <img
            src='/images/pages/services-hero.jpg'
            alt='Our Services'
            className='h-full w-full object-cover'
          />
          <div className='absolute inset-0 bg-linear-to-b from-gray-900/50 via-gray-900/80 to-gray-900' />
        </div>
        <div className='relative h-full mx-auto max-w-7xl px-4 sm:px-6'>
          <div className='flex h-full flex-col items-center justify-center text-center'>
            <h1 className='text-3xl font-bold text-white md:text-4xl lg:text-6xl'>
              Our Services
            </h1>
            <p className='mt-4 sm:mt-6 text-base sm:text-lg md:text-xl text-gray-200 max-w-3xl px-2 sm:px-0'>
              Explore our comprehensive range of professional services designed
              to meet all your needs
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='relative mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-8 md:py-16'>
        {/* Category Navigation */}
        <div className='mb-6 sm:mb-8 md:mb-12 overflow-x-auto scrollbar-hide pb-4'>
          <div className='flex space-x-2 md:space-x-4 min-w-max'>
            {serviceCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`relative group px-3 sm:px-4 md:px-6 py-2 md:py-3 rounded-full transition-all duration-300 ${
                  activeCategory === category.id
                    ? `bg-linear-to-r ${category.gradientFrom} ${
                        category.gradientTo
                      } text-white shadow-md shadow-${category.gradientFrom.replace(
                        'from-',
                        ''
                      )}/30`
                    : 'bg-gray-800/50 text-gray-300 hover:bg-gray-800 hover:shadow-md hover:shadow-blue-500/10'
                }`}
              >
                <div className='flex items-center space-x-1 md:space-x-2'>
                  <category.icon
                    className={`h-4 w-4 md:h-5 md:w-5 ${
                      activeCategory === category.id
                        ? 'text-white'
                        : category.color
                    }`}
                  />
                  <span className='text-xs sm:text-sm md:text-base'>
                    {category.title}
                  </span>
                </div>
                {activeCategory !== category.id && (
                  <span className='absolute bottom-0 left-0 h-0.5 w-0 bg-linear-to-r from-blue-400 to-purple-400 transition-all duration-300 group-hover:w-full'></span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Category Description */}
        {activeCategoryData && (
          <div className='mb-6 sm:mb-8 md:mb-12'>
            <div className='bg-gray-800/30 backdrop-blur-xs rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 border border-gray-700/30 hover:border-gray-700/50 transition-colors duration-300'>
              <div className='flex items-start space-x-3 md:space-x-4'>
                <div
                  className={`rounded-xl bg-gray-800/70 p-2 md:p-3 ${activeCategoryData.color} transform transition-all duration-300 hover:scale-110`}
                >
                  <activeCategoryData.icon className='h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8' />
                </div>
                <div>
                  <h2 className='text-lg sm:text-xl md:text-2xl font-bold text-white'>
                    {activeCategoryData.title}
                  </h2>
                  <p className='mt-1 md:mt-2 text-xs sm:text-sm md:text-base text-gray-300'>
                    {activeCategoryData.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Services Grid */}
        {activeCategoryData && (
          <motion.div
            className='grid gap-3 sm:gap-4 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-2'
            variants={containerVariants}
            initial='hidden'
            animate={isLoaded ? 'visible' : 'hidden'}
          >
            {activeCategoryData.services.map((service) => (
              <motion.div
                key={service.id}
                className='group relative isolate overflow-hidden rounded-xl sm:rounded-2xl bg-gray-800/30 backdrop-blur-xs border border-gray-700/30 hover:bg-gray-800/50 transition-all duration-300 hover:border-gray-600/50 hover:shadow-lg hover:shadow-blue-900/20'
                variants={itemVariants}
              >
                <div
                  className='absolute -inset-1 bg-linear-to-br opacity-0 group-hover:opacity-20 transition-opacity duration-300 -z-10'
                  style={{
                    background: `linear-gradient(to bottom right, ${service.overlayColor
                      .replace('from-', '')
                      .replace('/80', '')}, #000)`,
                  }}
                />

                <div className='p-3 sm:p-4 md:p-6'>
                  <div className='flex flex-col sm:flex-row sm:items-start sm:space-x-4'>
                    {/* Icon */}
                    <div className='shrink-0 rounded-xl bg-gray-800/70 p-2 sm:p-3 transition-transform duration-300 group-hover:scale-110 mb-3 sm:mb-0'>
                      <service.icon className='h-5 w-5 sm:h-6 sm:w-6 text-gray-300 group-hover:text-blue-400 transition-colors duration-300' />
                    </div>

                    {/* Content */}
                    <div className='flex-1'>
                      <h3 className='text-base sm:text-lg md:text-xl font-bold text-white group-hover:text-blue-400 transition-colors duration-300'>
                        {service.title}
                      </h3>
                      <p className='mt-1 md:mt-2 text-xs sm:text-sm md:text-base text-gray-300'>
                        {service.description}
                      </p>

                      {/* Features */}
                      <ul className='mt-2 sm:mt-3 md:mt-4 space-y-1 md:space-y-2'>
                        {service.features.map((feature, idx) => (
                          <li key={idx} className='flex items-start space-x-2'>
                            <LuChevronRight className='h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 text-blue-400 shrink-0 mt-0.5 group-hover:text-blue-300 transition-colors duration-300' />
                            <span className='text-xs sm:text-sm md:text-base text-gray-300 group-hover:text-gray-200 transition-colors duration-300'>
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>

                      {/* Link */}
                      <div className='mt-3 sm:mt-4 md:mt-6'>
                        <Link
                          href={service.url}
                          className='inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors text-xs sm:text-sm md:text-base group'
                        >
                          Learn more
                          <LuArrowRight className='ml-1 md:ml-2 h-3 w-3 md:h-4 md:w-4 transform group-hover:translate-x-1 transition-transform duration-300' />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Contact CTA */}
        <div className='mt-8 sm:mt-12 md:mt-20 text-center'>
          <div className='bg-linear-to-r from-blue-900/30 to-purple-900/30 rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-10 backdrop-blur-xs border border-blue-700/20 hover:border-blue-700/40 transition-colors duration-300'>
            <h2 className='text-xl sm:text-2xl md:text-3xl font-bold text-white'>
              Ready to get started?
            </h2>
            <p className='mt-2 sm:mt-3 md:mt-4 text-sm sm:text-base md:text-xl text-gray-300 max-w-2xl mx-auto px-2 sm:px-4'>
              Contact our team today to discuss how we can help with your
              specific needs
            </p>
            <div className='mt-4 sm:mt-6 md:mt-8'>
              <Link
                href='/contact'
                className='inline-flex items-center space-x-2 bg-linear-to-r from-blue-500 to-purple-500 px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 rounded-full text-white text-sm sm:text-base font-medium transition-all duration-300 hover:from-blue-600 hover:to-purple-600 hover:shadow-lg hover:shadow-blue-500/20 hover:scale-105'
              >
                <span>Contact Us</span>
                <LuArrowRight className='h-4 w-4 md:h-5 md:w-5' />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesPage;
